import React, { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import SuccessModal from "../components/SuccessModal";
import ErrorModal from "../components/ErrorModal";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  getCountries, 
  getSocialProductCategories, 
  getSocialProductTypes, 
  getSocialProducts,
  activateSocialProduct 
} from "../../../../services/socialProductsService";
import { toast } from "react-toastify";
import { 
  X, 
  Search, 
  ChevronDown, 
  Globe, 
  MessageSquare, 
  ShoppingCart,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  TrendingUp,
  Users,
  Heart,
  Share2,
  Filter,
  Grid,
  List,
  Info,
  Phone,
  AtSign,
  Hash,
  Link as LinkIcon,
  DollarSign,
  Package,
  Zap,
  ThumbsUp,
  Send,
  TrendingDown,
  Percent
} from "lucide-react";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "900px",
      maxWidth: "95vw",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 16,
      padding: theme.spacing(3),
      backgroundColor: isDark ? "#111827" : theme.palette.background.paper,
    },
  };
});

const getPlatformIcon = (platform) => {
  const icons = {
    'facebook': <ThumbsUp className="text-blue-600 dark:text-blue-400" size={20} />,
    'instagram': <Heart className="text-pink-600 dark:text-pink-400" size={20} />,
    'whatsapp': <MessageSquare className="text-green-600 dark:text-green-400" size={20} />,
    'twitter': <Share2 className="text-sky-600 dark:text-sky-400" size={20} />,
    'tiktok': <TrendingUp className="text-gray-900 dark:text-gray-300" size={20} />,
    'telegram': <Send className="text-blue-600 dark:text-blue-400" size={20} />,
    'snapchat': <Zap className="text-yellow-600 dark:text-yellow-400" size={20} />,
    'youtube': <Eye className="text-red-600 dark:text-red-400" size={20} />
  };
  return icons[platform?.toLowerCase()] || <Package size={20} />;
};

const getAccountPlaceholder = (platform, t) => {
  const placeholders = {
    'facebook': t('socialActivationModal.placeholders.facebook'),
    'instagram': t('socialActivationModal.placeholders.instagram'),
    'whatsapp': t('socialActivationModal.placeholders.whatsapp'),
    'twitter': t('socialActivationModal.placeholders.twitter'),
    'tiktok': t('socialActivationModal.placeholders.tiktok'),
    'telegram': t('socialActivationModal.placeholders.telegram'),
    'snapchat': t('socialActivationModal.placeholders.snapchat'),
    'youtube': t('socialActivationModal.placeholders.youtube')
  };
  return placeholders[platform?.toLowerCase()] || t('socialActivationModal.placeholders.default');
};

const validateAccount = (account, platform, t) => {
  if (!account || !account.trim()) {
    return { isValid: false, message: t('socialActivationModal.validation.accountRequired') };
  }

  const trimmed = account.trim();
  const platformLower = platform?.toLowerCase();

  if (platformLower === 'whatsapp') {
    if (!/^\d{9,12}$/.test(trimmed.replace(/\D/g, ''))) {
      return { isValid: false, message: t('socialActivationModal.validation.invalidWhatsapp') };
    }
    return { isValid: true, message: "" };
  }

  if (trimmed.includes('@') && !trimmed.includes('.')) {
    return { isValid: false, message: t('socialActivationModal.validation.invalidEmail') };
  }

  if (trimmed.length < 3) {
    return { isValid: false, message: t('socialActivationModal.validation.minLength') };
  }

  if (trimmed.length > 100) {
    return { isValid: false, message: t('socialActivationModal.validation.maxLength') };
  }

  if (/[<>{}[\]\\\/]/.test(trimmed)) {
    return { isValid: false, message: t('socialActivationModal.validation.invalidChars') };
  }

  return { isValid: true, message: "" };
};

export default function SocialActivationModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); 
  const [socialAccount, setSocialAccount] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activationResponse, setActivationResponse] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: "",
    maxPrice: "",
    sortBy: "popularity" 
  });
  const IMG_BASE_URL = import.meta.env.VITE_IMG_BASE_URL2;

  // Dark mode detection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const { data: countriesData, isLoading: countriesLoading } = useQuery({
    queryKey: ["countries"],
    queryFn: getCountries,
    enabled: open,
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["socialProductCategories"],
    queryFn: getSocialProductCategories,
    enabled: open,
  });

  const { data: typesData, isLoading: typesLoading } = useQuery({
    queryKey: ["socialProductTypes"],
    queryFn: getSocialProductTypes,
    enabled: open,
  });

  const { 
    data: productsData, 
    isLoading: productsLoading,
    refetch: refetchProducts 
  } = useQuery({
    queryKey: ["socialProducts", selectedCountry?.id, selectedCategory?.id, selectedType?.id, searchQuery, filters],
    queryFn: () => getSocialProducts({
      countryId: selectedCountry?.id,
      productCategoryId: selectedCategory?.id,
      productTypeId: selectedType?.id,
      search: searchQuery,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      sortBy: filters.sortBy
    }),
    enabled: open,
  });

  const activateMutation = useMutation({
    mutationFn: activateSocialProduct,
    onSuccess: (data) => {
      setActivationResponse(data);
      setIsTransferSuccess(true);
      toast.success(t('socialActivationModal.toasts.activationSuccess'));
    },
    onError: (error) => {
      console.error("Activation error:", error);
      const message = error?.response?.data?.error || t('socialActivationModal.toasts.activationFailed');
      setErrorMessage(message);
      setIsError(true);
      toast.error(message);
    },
  });

  useEffect(() => {
    if (countriesData?.data?.length > 0 && !selectedCountry) {
      const afghanistan = countriesData.data.find(c => c.countryCode === "AF") || countriesData.data[0];
      setSelectedCountry(afghanistan);
    }
  }, [countriesData]);

  useEffect(() => {
    if (categoriesData?.data?.length > 0 && !selectedCategory) {
      setSelectedCategory(categoriesData.data[0]);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (typesData?.data?.length > 0 && !selectedType) {
      setSelectedType(typesData.data[0]);
    }
  }, [typesData]);

  const handleReset = () => {
    setStep(1);
    setSelectedProduct(null);
    setSocialAccount("");
    setValidationError("");
    setSearchQuery("");
    setActivationResponse(null);
    setShowPriceBreakdown(false);
    setFilters({
      minPrice: "",
      maxPrice: "",
      sortBy: "popularity"
    });
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setStep(2);
  };

  const handleAccountChange = (e) => {
    const value = e.target.value;
    setSocialAccount(value);
    
    if (value.trim()) {
      const validation = validateAccount(value, selectedProduct?.platform, t);
      setValidationError(validation.isValid ? "" : validation.message);
    } else {
      setValidationError("");
    }
  };

  const handleNext = () => {
    if (step === 2) {
      if (!socialAccount.trim()) {
        setValidationError(t('socialActivationModal.validation.accountRequired'));
        return;
      }
      
      const validation = validateAccount(socialAccount, selectedProduct?.platform, t);
      if (!validation.isValid) {
        setValidationError(validation.message);
        return;
      }
      
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleActivate = () => {
    if (!selectedProduct || !socialAccount.trim()) {
      toast.error(t('socialActivationModal.toasts.selectProduct'));
      return;
    }

    const payload = {
      productId: selectedProduct.id,
      receiver: socialAccount.trim(),
      countryId: selectedCountry?.id,
    };

    activateMutation.mutate(payload);
  };

  const closeSuccessModal = () => {
    setIsTransferSuccess(false);
    onSuccess();
    handleReset();
    onClose();
  };

  const closeErrorModal = () => {
    setIsError(false);
    setErrorMessage("");
  };

  const getFilteredProducts = () => {
    if (!productsData?.data) return [];
    
    let filtered = [...productsData.data];
    
    if (filters.minPrice) {
      filtered = filtered.filter(p => parseFloat(p.price || p.originalPrice) >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(p => parseFloat(p.price || p.originalPrice) <= parseFloat(filters.maxPrice));
    }
    
    switch (filters.sortBy) {
      case 'price_low':
        filtered.sort((a, b) => parseFloat(a.price || a.originalPrice) - parseFloat(b.price || b.originalPrice));
        break;
      case 'price_high':
        filtered.sort((a, b) => parseFloat(b.price || b.originalPrice) - parseFloat(a.price || a.originalPrice));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default: 
        filtered.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
    }
    
    return filtered;
  };

  const filteredProducts = getFilteredProducts();


  const getDisplayPrice = (product) => {
    return product.price || product.originalPrice || product.basePrice || 0;
  };


  const getPriceBreakdown = (product) => {
    if (product.priceBreakdown) {
      return product.priceBreakdown;
    }
    return null;
  };

  return (
    <>
      <StyledDialog open={open} onClose={onClose}>
        <div className="px-5 py-3">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className={`text-xl font-semibold transition-colors duration-300 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {step === 1 && t('socialActivationModal.step1.title')}
                {step === 2 && t('socialActivationModal.step2.title')}
                {step === 3 && t('socialActivationModal.step3.title')}
              </h3>
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {step === 1 && t('socialActivationModal.step1.subtitle')}
                {step === 2 && t('socialActivationModal.step2.subtitle', { product: selectedProduct?.productName?.en || t('socialActivationModal.selectedProduct') })}
                {step === 3 && t('socialActivationModal.step3.subtitle')}
              </p>
            </div>
            <button
              onClick={onClose}
              className={`transition-colors ${
                isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <X size={24} />
            </button>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div className={`p-4 rounded-lg space-y-4 transition-colors duration-300 ${
                isDark ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.country')}
                    </label>
                    <select
                      value={selectedCountry?.id || ""}
                      onChange={(e) => {
                        const country = countriesData?.data?.find(c => c.id === e.target.value);
                        setSelectedCountry(country);
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {countriesData?.data?.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.countryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.category')}
                    </label>
                    <select
                      value={selectedCategory?.id || ""}
                      onChange={(e) => {
                        const category = categoriesData?.data?.find(c => c.id === e.target.value);
                        setSelectedCategory(category);
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {categoriesData?.data?.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.categoryName?.en || category.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.productType')}
                    </label>
                    <select
                      value={selectedType?.id || ""}
                      onChange={(e) => {
                        const type = typesData?.data?.find(t => t.id === e.target.value);
                        setSelectedType(type);
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {typesData?.data?.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.typeName?.en || type.typeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.sortBy')}
                    </label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="popularity">{t('socialActivationModal.sortOptions.popularity')}</option>
                      <option value="price_low">{t('socialActivationModal.sortOptions.priceLow')}</option>
                      <option value="price_high">{t('socialActivationModal.sortOptions.priceHigh')}</option>
                      <option value="newest">{t('socialActivationModal.sortOptions.newest')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.minPrice')}
                    </label>
                    <input
                      type="number"
                      value={filters.minPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                      placeholder="0"
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.maxPrice')}
                    </label>
                    <input
                      type="number"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                      placeholder="10000"
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.filters.search')}
                    </label>
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        isDark ? 'text-gray-400' : 'text-gray-400'
                      }`} size={16} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('socialActivationModal.filters.searchPlaceholder')}
                        className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <p className={`text-sm transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {t('socialActivationModal.productsCount', { count: filteredProducts.length })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === "grid" 
                          ? "bg-[#CD0C02] text-white" 
                          : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <Grid size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === "list" 
                          ? "bg-[#CD0C02] text-white" 
                          : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <List size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {productsLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#CD0C02]"></div>
                  <p className={`mt-4 transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('socialActivationModal.loading')}
                  </p>
                </div>
              ) : filteredProducts.length > 0 ? (
                viewMode === "grid" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto p-1">
                    {filteredProducts.map((product) => {
                      const displayPrice = getDisplayPrice(product);
                      const priceBreakdown = getPriceBreakdown(product);
                      const originalPrice = product.originalPrice || product.basePrice;
                      const hasDiscount = originalPrice && displayPrice < originalPrice;
                      
                      return (
                        <div
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className={`border rounded-xl p-4 cursor-pointer transition-all group ${
                            isDark 
                              ? 'border-gray-700 hover:border-[#CD0C02] hover:shadow-lg' 
                              : 'border-gray-200 hover:border-[#CD0C02] hover:shadow-lg'
                          }`}
                        >
                          {product.image && (
                            <img src={`${IMG_BASE_URL}/${product.image}`} className="rounded-[6px] mb-2 w-full h-32 object-cover" alt="Product" />
                          )}
                          
                          <h4 className={`font-semibold mb-1 transition-colors duration-300 group-hover:text-[#CD0C02] ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {product.productName?.en || product.productName}
                          </h4>
                          
                          <p className={`text-sm mb-3 line-clamp-2 transition-colors duration-300 ${
                            isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {product.description || t('socialActivationModal.noDescription')}
                          </p>
                          
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              {/* {originalPrice && originalPrice !== displayPrice && (
                                <span className="text-xs line-through text-gray-400 mr-2">
                                  {parseFloat(originalPrice).toFixed(2)} AFN
                                </span>
                              )} */}
                              <span className="text-lg font-bold text-[#CD0C02]">
                                {parseFloat(displayPrice).toFixed(2)} AFN
                              </span>
                            </div>
                            <div className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              <Clock size={14} />
                              <span>{product.deliveryTime || t('socialActivationModal.instant')}</span>
                            </div>
                          </div>

                          {/* {priceBreakdown && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowPriceBreakdown(!showPriceBreakdown);
                                }}
                                className={`text-xs flex items-center gap-1 transition-colors duration-300 ${
                                  isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                <Percent size={12} />
                                {t('socialActivationModal.priceBreakdown')}
                              </button>
                            </div>
                          )} */}

                          {product.minQuantity && product.maxQuantity && (
                            <div className={`mt-2 text-xs transition-colors duration-300 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {t('socialActivationModal.quantityRange', { min: product.minQuantity, max: product.maxQuantity })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto p-1">
                    {filteredProducts.map((product) => {
                      const displayPrice = getDisplayPrice(product);
                      const originalPrice = product.originalPrice || product.basePrice;
                      const priceBreakdown = getPriceBreakdown(product);
                      
                      return (
                        <div
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          className={`border rounded-lg p-4 cursor-pointer transition-all flex items-center gap-4 ${
                            isDark 
                              ? 'border-gray-700 hover:border-[#CD0C02] hover:shadow-md' 
                              : 'border-gray-200 hover:border-[#CD0C02] hover:shadow-md'
                          }`}
                        >
                          <div className={`p-3 rounded-lg ${product.platform ? 'bg-opacity-10' : isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            {getPlatformIcon(product.platform)}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-semibold transition-colors duration-300 ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {product.productName?.en || product.productName}
                              </h4>
                              {product.popularity > 80 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDark 
                                    ? 'bg-yellow-900/30 text-yellow-300' 
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {t('socialActivationModal.popular')}
                                </span>
                              )}
                            </div>
                            
                            <p className={`text-sm mb-2 transition-colors duration-300 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {product.description || t('socialActivationModal.noDescription')}
                            </p>
                            
                            <div className="flex items-center gap-4 text-sm">
                              <div>
                                {/* {originalPrice && originalPrice !== displayPrice && (
                                  <span className="text-xs line-through text-gray-400 mr-2">
                                    {parseFloat(originalPrice).toFixed(2)} AFN
                                  </span>
                                )} */}
                                <span className="font-bold text-[#CD0C02]">
                                  {parseFloat(displayPrice).toFixed(2)} AFN
                                </span>
                              </div>
                              <span className={`flex items-center gap-1 transition-colors duration-300 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                <Clock size={14} />
                                {product.deliveryTime || t('socialActivationModal.instant')}
                              </span>
                              {priceBreakdown && (
                                <span className={`text-xs flex items-center gap-1 transition-colors duration-300 ${
                                  isDark ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  <Percent size={12} />
                                  {priceBreakdown.revenueRate > 0 && `+${priceBreakdown.revenueRate}%`}
                                  {priceBreakdown.serviceRate > 0 && ` +${priceBreakdown.serviceRate}%`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <div className="text-center py-12">
                  <Package className={`mx-auto mb-3 ${
                    isDark ? 'text-gray-600' : 'text-gray-300'
                  }`} size={48} />
                  <p className={`transition-colors duration-300 ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {t('socialActivationModal.noProducts')}
                  </p>
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDark ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {t('socialActivationModal.adjustFilters')}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && selectedProduct && (
            <div className="space-y-6">
              <div className={`bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg ${
                isDark ? 'from-gray-800 to-gray-700' : ''
              }`}>
                <div className="flex items-start gap-4">
                  {selectedProduct.image && (
                    <img src={`${IMG_BASE_URL}/${selectedProduct.image}`} className="rounded-[6px] w-[100px] h-[100px] object-cover" alt="Product" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {selectedProduct.productName?.en || selectedProduct.productName}
                    </h4>
                    <p className={`text-sm mt-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {selectedProduct.description || t('socialActivationModal.noDescription')}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      {/* {selectedProduct.originalPrice && selectedProduct.originalPrice !== selectedProduct.price && (
                        <span className="text-sm line-through text-gray-400">
                          {parseFloat(selectedProduct.originalPrice).toFixed(2)} AFN
                        </span>
                      )} */}
                      <span className="text-lg font-bold text-[#CD0C02]">
                        {parseFloat(selectedProduct.price || selectedProduct.originalPrice).toFixed(2)} AFN
                      </span>
                      <span className={`text-sm flex items-center gap-1 transition-colors duration-300 ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <Clock size={14} />
                        {selectedProduct.deliveryTime || t('socialActivationModal.instant')}
                      </span>
                    </div>
                    
               
                    {/* {selectedProduct.priceBreakdown && (
                      <div className="mt-3">
                        <button
                          onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                          className={`text-xs flex items-center gap-1 transition-colors duration-300 ${
                            isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Percent size={12} />
                          {t('socialActivationModal.showPriceBreakdown')}
                        </button>
                        
                        {showPriceBreakdown && (
                          <div className={`mt-2 p-3 rounded-lg text-xs space-y-1 ${
                            isDark ? 'bg-gray-700' : 'bg-gray-100'
                          }`}>
                            <div className="flex justify-between">
                              <span>{t('socialActivationModal.basePrice')}:</span>
                              <span>{parseFloat(selectedProduct.priceBreakdown.basePrice).toFixed(2)} AFN</span>
                            </div>
                            {selectedProduct.priceBreakdown.revenueRate > 0 && (
                              <div className="flex justify-between">
                                <span>{t('socialActivationModal.revenueCharge')} ({selectedProduct.priceBreakdown.revenueRate}%):</span>
                                <span className="text-orange-500">+{parseFloat(selectedProduct.priceBreakdown.revenueAmount).toFixed(2)} AFN</span>
                              </div>
                            )}
                            {selectedProduct.priceBreakdown.serviceRate > 0 && (
                              <div className="flex justify-between">
                                <span>{t('socialActivationModal.serviceCharge')} ({selectedProduct.priceBreakdown.serviceRate}%):</span>
                                <span className="text-orange-500">+{parseFloat(selectedProduct.priceBreakdown.serviceAmount).toFixed(2)} AFN</span>
                              </div>
                            )}
                            {selectedProduct.priceBreakdown.commissionRate > 0 && (
                              <div className="flex justify-between">
                                <span>{t('socialActivationModal.commissionDiscount')} ({selectedProduct.priceBreakdown.commissionRate}%):</span>
                                <span className="text-green-500">-{parseFloat(selectedProduct.priceBreakdown.commissionAmount).toFixed(2)} AFN</span>
                              </div>
                            )}
                            <div className={`pt-1 mt-1 font-bold flex justify-between border-t ${
                              isDark ? 'border-gray-600' : 'border-gray-300'
                            }`}>
                              <span>{t('socialActivationModal.totalAmount')}:</span>
                              <span className="text-[#CD0C02]">{parseFloat(selectedProduct.priceBreakdown.total).toFixed(2)} AFN</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )} */}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t('socialActivationModal.accountDetails')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                      isDark ? 'text-gray-400' : 'text-gray-400'
                    }`}>
                      <AtSign size={18} />
                    </div>
                    <input
                      type="text"
                      value={socialAccount}
                      onChange={handleAccountChange}
                      placeholder={getAccountPlaceholder(selectedProduct.platform, t)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:border-[#CD0C02] focus:outline-none focus:ring-1 focus:ring-[#CD0C02] transition-colors duration-300 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      autoFocus
                    />
                  </div>
                  {validationError ? (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {validationError}
                    </p>
                  ) : (
                    <p className={`text-sm mt-1 transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {t('socialActivationModal.accountExample', { example: selectedProduct.platform === 'whatsapp' ? '700000000' : 'johndoe' })}
                    </p>
                  )}
                </div>

                <div className={`p-4 rounded-lg ${
                  isDark ? 'bg-blue-900/20' : 'bg-blue-50'
                }`}>
                  <h5 className={`font-medium mb-2 flex items-center gap-2 ${
                    isDark ? 'text-blue-300' : 'text-blue-800'
                  }`}>
                    <Info size={16} />
                    {t('socialActivationModal.instructions.title', { platform: selectedProduct.platform || t('socialActivationModal.instructions.defaultPlatform') })}
                  </h5>
                  <ul className={`text-sm space-y-1 list-disc pl-5 ${
                    isDark ? 'text-blue-300' : 'text-blue-700'
                  }`}>
                    <li>{t('socialActivationModal.instructions.li1')}</li>
                    <li>{t('socialActivationModal.instructions.li2')}</li>
                    <li>{t('socialActivationModal.instructions.li3')}</li>
                    <li>{t('socialActivationModal.instructions.li4')}</li>
                    <li>{t('socialActivationModal.instructions.li5')}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {step === 3 && selectedProduct && (
            <div className="space-y-6">
              <div className={`rounded-lg p-6 ${
                isDark ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <h4 className={`font-semibold mb-4 transition-colors duration-300 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {t('socialActivationModal.orderSummary')}
                </h4>
                
                <div className="space-y-4">
                  <div className={`flex items-start gap-3 pb-4 border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <div className={`p-2 rounded-lg ${selectedProduct.platform ? 'bg-opacity-10' : isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      {getPlatformIcon(selectedProduct.platform)}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium transition-colors duration-300 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {selectedProduct.productName?.en || selectedProduct.productName}
                      </p>
                      <p className={`text-sm mt-1 transition-colors duration-300 ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {selectedProduct.description || t('socialActivationModal.socialService')}
                      </p>
                    </div>
                    <div className="text-right">
                      {/* {selectedProduct.originalPrice && selectedProduct.originalPrice !== selectedProduct.price && (
                        <p className="text-xs line-through text-gray-400">
                          {parseFloat(selectedProduct.originalPrice).toFixed(2)} AFN
                        </p>
                      )} */}
                      <p className="font-bold text-[#CD0C02]">
                        {parseFloat(selectedProduct.price || selectedProduct.originalPrice).toFixed(2)} AFN
                      </p>
                    </div>
                  </div>

                  <div className={`flex justify-between items-center py-3 border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <span className={`transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {t('socialActivationModal.targetAccount')}:
                    </span>
                    <span className={`font-medium flex items-center gap-2 transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      <AtSign size={16} className={isDark ? 'text-gray-400' : 'text-gray-400'} />
                      {socialAccount}
                    </span>
                  </div>

                  <div className={`flex justify-between items-center py-3 border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <span className={`transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {t('socialActivationModal.platform')}:
                    </span>
                    <span className={`font-medium capitalize transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {selectedProduct.platform || t('socialActivationModal.socialMedia')}
                    </span>
                  </div>

                  <div className={`flex justify-between items-center py-3 border-b ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <span className={`transition-colors duration-300 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {t('socialActivationModal.estimatedDelivery')}:
                    </span>
                    <span className={`font-medium flex items-center gap-1 transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      <Clock size={16} className={isDark ? 'text-gray-400' : 'text-gray-400'} />
                      {selectedProduct.deliveryTime || t('socialActivationModal.defaultDelivery')}
                    </span>
                  </div>

                  {/* {selectedProduct.priceBreakdown && (
                    <div className={`py-3 border-b ${
                      isDark ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                            {t('socialActivationModal.basePrice')}:
                          </span>
                          <span>{parseFloat(selectedProduct.priceBreakdown.basePrice).toFixed(2)} AFN</span>
                        </div>
                        {selectedProduct.priceBreakdown.revenueRate > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                              {t('socialActivationModal.revenueCharge')} ({selectedProduct.priceBreakdown.revenueRate}%):
                            </span>
                            <span className="text-orange-500">+{parseFloat(selectedProduct.priceBreakdown.revenueAmount).toFixed(2)} AFN</span>
                          </div>
                        )}
                        {selectedProduct.priceBreakdown.serviceRate > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                              {t('socialActivationModal.serviceCharge')} ({selectedProduct.priceBreakdown.serviceRate}%):
                            </span>
                            <span className="text-orange-500">+{parseFloat(selectedProduct.priceBreakdown.serviceAmount).toFixed(2)} AFN</span>
                          </div>
                        )}
                        {selectedProduct.priceBreakdown.commissionRate > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                              {t('socialActivationModal.commissionDiscount')} ({selectedProduct.priceBreakdown.commissionRate}%):
                            </span>
                            <span className="text-green-500">-{parseFloat(selectedProduct.priceBreakdown.commissionAmount).toFixed(2)} AFN</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )} */}

                  <div className="flex justify-between items-center pt-4">
                    <span className={`text-lg font-semibold transition-colors duration-300 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {t('socialActivationModal.totalAmount')}:
                    </span>
                    <span className="text-2xl font-bold text-[#CD0C02]">
                      {parseFloat(selectedProduct.price || selectedProduct.originalPrice).toFixed(2)} AFN
                    </span>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`flex-shrink-0 mt-0.5 ${
                    isDark ? 'text-yellow-400' : 'text-yellow-600'
                  }`} size={18} />
                  <div>
                    <h5 className={`font-medium mb-2 ${
                      isDark ? 'text-yellow-300' : 'text-yellow-800'
                    }`}>
                      {t('socialActivationModal.pleaseNote')}
                    </h5>
                    <ul className={`text-sm space-y-1 list-disc pl-5 ${
                      isDark ? 'text-yellow-300' : 'text-yellow-700'
                    }`}>
                      <li>{t('socialActivationModal.note.li1')}</li>
                      <li>{t('socialActivationModal.note.li2')}</li>
                      <li>{t('socialActivationModal.note.li3')}</li>
                      <li>{t('socialActivationModal.note.li4')}</li>
                      <li>{t('socialActivationModal.note.li5')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                onClick={handleBack}
                className={`flex-1 py-2.5 px-4 border rounded-lg transition-colors font-medium ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.back')}
              </button>
            )}
            
            {step === 1 ? (
              <button
                onClick={onClose}
                className={`flex-1 py-2.5 px-4 border rounded-lg transition-colors font-medium ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('common.cancel')}
              </button>
            ) : step === 2 ? (
              <button
                onClick={handleNext}
                disabled={!socialAccount.trim() || !!validationError}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  !socialAccount.trim() || !!validationError
                    ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed"
                    : "bg-[#CD0C02] hover:bg-[#b30b02] text-white"
                }`}
              >
                {t('common.continue')}
              </button>
            ) : (
              <button
                onClick={handleActivate}
                disabled={activateMutation.isPending}
                className="flex-1 py-2.5 px-4 bg-[#CD0C02] hover:bg-[#b30b02] text-white rounded-lg font-medium disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {activateMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('common.processing')}
                  </span>
                ) : (
                  t('socialActivationModal.confirmActivate')
                )}
              </button>
            )}
          </div>
        </div>
      </StyledDialog>

      <SuccessModal
        open={isTransferSuccess}
        onClose={closeSuccessModal}
        amount={selectedProduct?.price || selectedProduct?.originalPrice}
        title={t('socialActivationModal.success.title')}
        message={
          activationResponse?.message || 
          t('socialActivationModal.success.message')
        }
        transactionId={activationResponse?.txnNumber}
      />

      <ErrorModal
        open={isError}
        onClose={closeErrorModal}
        title={t('socialActivationModal.error.title')}
        error={errorMessage}
        onRetry={handleActivate}
      >
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={`transition-colors duration-300 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('socialActivationModal.product')}:
            </span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {selectedProduct?.productName?.en}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className={`transition-colors duration-300 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('socialActivationModal.account')}:
            </span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {socialAccount}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className={`transition-colors duration-300 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('socialActivationModal.amount')}:
            </span>
            <span className="font-medium text-[#CD0C02]">
              {parseFloat(selectedProduct?.price || selectedProduct?.originalPrice || 0).toFixed(2)} AFN
            </span>
          </div>
        </div>
      </ErrorModal>
    </>
  );
}